import { Component } from '@angular/core';
import { OnInit, OnDestroy, ViewChild, HostListener, ElementRef } from '@angular/core';
import { NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { ViewportService } from '../../services/viewport.service';
import { FacetsComponent } from '../../facets/facets/facets.component';
import { SearchService } from '../../search/services/search.service';
import { MapService } from '../../map/services/map.service';
import { RoutingService } from '../../services/routing.service';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

/**
 * App advanced search panel component.
 */
@Component({
	selector: 'kv-advanced',
	templateUrl: './advanced.component.html',
	styleUrl: './advanced.component.css'
})
export class AdvancedComponent implements OnInit, OnDestroy {
	private $state: any;

	private searchEventSubscription!: Subscription;
	private mapEventSubscription!: Subscription;
	private userEventSubscription!: Subscription;

	@ViewChild(FacetsComponent) facetsComponent!: FacetsComponent;
	@ViewChild('leftSidePanelHeader', { static: false }) leftSidePanelHeader!: ElementRef;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	initialized: boolean = false;
	loggedIn: boolean = false;
	mapEnabled: boolean = false;
	mapReady: boolean = false;
	browseEnabled: boolean = false;

	advancedConfig!: any;
	activeTab: string = 'c';
	tabs: any[] = [];
	dropdownOptions: any[] = [];
	newSearchActiveTab: string = 'c';
	endSearchActiveTab: string = 'c';

	// FIXME: These were originally set in search.controller.js
	// and picked up by the advanced.controller child view
	// Currently not working.
	states = {
		browseOpen: false,
		query: '',
		type: 16,
		currentView: ''
	};

	searchInputs: any[] = [];
	geoFilmstrip: boolean = false;
	filmstripRecords: any[] = [];
	filmstripRecordsView: any[] = [];

	dateFormatDisplay: string = 'MM/DD/YY';

	dragDestination = null;
	dragBefore = this.dragDestination;

	panelStyling: { height?: string } = {};

	constructor(
		private ngZone: NgZone,
		private configService: ConfigService,
		private userService: UserService,
		private searchService: SearchService,
		private mapService: MapService,
		private routingService: RoutingService,
		private viewportService: ViewportService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.loggedIn = this.userService?.user.loggedin;
	}

	// Debugging:
	// Triggered on every change detection cycle, invoked each time the DOM tree changes
	//	ngAfterViewChecked() {
	//		console.log('ngAfterViewChecked triggered', {
	//			activeTab: this.activeTab,
	//		});
	//		console.trace();
	//		debugger;
	//	}

	ngOnInit(): void {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'loggedIn') {
				console.log('Logged In event received!');
				this.buildSearchPanel();
				this.loggedIn = this.userService.user.loggedin;
			}
			else if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
				this.initTabs(); // Tab labels depend on LOCALIZATION
			}
			else if (event === "userPrefsUpdate") {
				this.userPermissions = this.userService.getUserPermissionsMap();
				this.updateTabVisibility();
				this.buildSearchPanel();
			}
		});

		this.mapEventSubscription = this.mapService.mapServiceEvent.subscribe((event: any) => {
			if ('mapReady' === event.eventType) {
				this.mapReady = true;
				this.updateTabVisibility();
				this.activeTab = "m";
			}
			else if ('openRecordInMap' === event.eventType) {
				this.checkMapReadyAndSetTab();
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEnded') {
				console.log('Search Ended event received!');

				let showTab = this.states.currentView === 'M' ? 'm' : this.endSearchActiveTab;
				this.showDefaultTab(showTab);
			}
			else if (event.type === 'clearSearch') {
				console.log('Search Clear event received!');

				this.processSearchParms();
				this.showDefaultTab(this.newSearchActiveTab);
			}
			else if (event.type === 'SearchParmsUpdated') {
				console.log('Search Parms Updated event received!');

				this.buildSearchPanel();
			}
			else if (event.type === 'searchResultField') {
				console.log('Search Result Field event received!');

				this.listenToSearchResultFieldEvent(event.data);
			}
			else if (event.type === 'toggleAdvanced') {
				console.log('ToggleAdvanced event received!');
				if (this.states.currentView === 'M' && this.activeTab !== 'm' && event.data.toggle === 'M' && this.filmstripRecords && this.filmstripRecords.length > 0) {
					this.filmstripRecordsView = this.geoFilmstrip ? this.filmstripRecords.filter(record => { return record.geo }) : this.filmstripRecords;
					this.setActive('m');
				}
			}
			else if (event.type === 'dragLeaveDestination') {
				console.log('dragLeaveDestination event received!');
				this.listenToDragLeaveDestinationEvent(event.data);
			}
			else if (event.type === 'dragTarget') {
				console.log('dragTarget event received!');
				this.listenToDragTargetEvent(event.data);
			}
			else if (event.type === 'dragDestination') {
				console.log('dragDestination event received!');
				this.listenToDragDestinationEvent(event.data);
			} 
			else if(event.type === 'openBrowseLink') {
				this.listenToBrowseLinkEvent(event.data);
			}
		});

		let dateTypeProperty = this.userService.user?.properties?.awpropertyObjectList?.find((x: any) => x.name == 'USER_INTERFACE_DATE_TYPE');
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.mapEnabled = this.configService.getConfig()?.esri.enabled;
		this.browseEnabled = this.configService.getConfig()?.browse.enabled;

		this.advancedConfig = this.configService.getConfig().advancedSearchPanel;
		this.newSearchActiveTab = this.advancedConfig?.searchCriteria.id;
		this.endSearchActiveTab = this.advancedConfig?.searchCriteria.id;
		Object.values(this.advancedConfig).forEach((value: any) => {
			if (value.showOnNewSearch && value.enabled) {
				this.newSearchActiveTab = value.id;
			}
			if (value.showOnEndSearch && value.enabled) {
				this.endSearchActiveTab = value.id;
			}
		});

		this.initTabs();

		console.log('Advanced component init');

		this.buildSearchPanel();

		const advancedStateParam = { ...this.routingService.getQueryParams() };
		this.setActive(advancedStateParam);
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}

		if (this.mapEventSubscription) {
			this.mapEventSubscription.unsubscribe();
		}
	}

	private checkMapReadyAndSetTab(maxAttempts = 20, delay = 200): void {
		let attempts = 0;

		const check = () => {
			if (this.mapReady) {
				console.log("Map is ready, changing tab.");
				this.activeTab = "m";
				return;
			}

			if (++attempts >= maxAttempts) {
				console.warn("Map not ready after max tries.");
				return;
			}

			setTimeout(check, delay);
		};

		check();
	}

    private listenToBrowseLinkEvent(data: any): void {
		this.activeTab = 'b';
		this.toggleTab();
	}
	
	private listenToSearchResultFieldEvent(data: any): void {
		// TODO Shouldn't this be in userService or searchService?
		// clean search criteria
		const user = this.userService.user;
		if (user && !user.searchInputs?.length) {
			user?.searchInputs?.forEach((currType: any) => {
				let value;
				if (currType.type && currType.type.toUpperCase() == "LIST_MULTI") {
					value = [];
				}
				else if (currType.type == "STRING") {
					value = [{ content: null }];
				}
				else {
					value = null;
				}

				this.searchService.updateValue(currType, { content: value });
				currType.updateFn(currType);
				currType.solrstring = null;
			});
		}

		this.facetsComponent.triggerFacet({ name: data.field }, { value: data.value });
	}

	private initTabs(): void {
		this.tabs = [{
			id: 'c',
			title: this.LOCALIZATION.searchCriteriaFilters || "Filters",
			hide: false
		},
		{
			id: 'f',
			title: this.LOCALIZATION.facets || "Facets",
			hide: true
		},
		{
			id: 'b',
			title: this.LOCALIZATION.browse || "Browse",
			hide: true
		}];

		this.activeTab = this.tabs[0].id;


		this.dropdownOptions = [{
			id: 's',
			title: this.LOCALIZATION.searches || "My Searches",
			hide: true
		},
		{
			id: 'm',
			title: this.LOCALIZATION.mapResults || "Map Results",
			hide: true
		},
		{
			id: 'a',
			title: this.LOCALIZATION.areaOfInterest || "Area of Interest",
			hide: true
		}];

		this.updateTabVisibility();
	}

	private updateTabVisibility(): void {
		this.tabs.forEach(option => {
			if (option.id === 'c') {
				option.hide = !this.advancedConfig.searchCriteria.enabled;
			}
			else if (option.id === 'f') {
				option.hide = (!this.advancedConfig.facets.enabled || this.userPermissions['BLOCK_FACETS']);
			}
			else if (option.id === 'b') {
				option.hide = !this.browseEnabled;
			}
		});

		this.dropdownOptions.forEach(option => {
			if (option.id === 's') {
				option.hide = !this.advancedConfig.savedSearches.enabled;
			}
			else if (option.id === 'm') {
				option.hide = (!this.mapEnabled || !this.mapReady);
			}
			else if (option.id === 'a') {
				option.hide = (!this.advancedConfig.areaOfInterest.enabled || !this.mapEnabled || !this.mapReady);
			}
		});
	}

	private buildSearchPanel(): void {
		console.log('Inside buildSearchPanel');

		this.searchInputs = [];

		if (!this.userService.user || !this.userService.user.searchInputs) {
			return;
		}

		this.searchInputs = this.userService.user.searchInputs;

		this.userService.getUserExtendedPrefs().then(
			(userPrefsObj: any) => {
				// Reorder the fields based on these settings
				let prefs: any = {};
				try {
					const rawPrefs = userPrefsObj.prefs || '{}';
					prefs = JSON.parse(rawPrefs);
				} catch (e) {
					console.error('Invalid JSON in userPrefsObj.prefs:', e);
					prefs = {}; // fallback to empty object
				}
				var searchorder = prefs.searchFieldOrder || [];

				this.searchInputs.sort((a, b) => {
					const indexA = searchorder.indexOf(a.name);
					const indexB = searchorder.indexOf(b.name);

					if (indexA === -1 && indexB === -1) {
						return 0;
					} else if (indexA === -1) {
						return 1;
					} else if (indexB === -1) {
						return -1;
					} else {
						return indexA - indexB;
					}
				});

				this.processSearchParms();
			},
			(err: any) => {
				console.error("Failed to fetch user extended details.", err);

				this.processSearchParms();
			}
		);
	}

	private processSearchParms(): void {
		const user = this.userService.user;
		if (!user || !user.systemPreferences?.length) {
			return;
		}

		let criteria = this.searchService.criteria;

		console.log(`Inside processSearchParms(), Content:`);
		console.log(criteria);

		for (let i = 0; i < user.systemPreferences.length; i++) {
			let currInput = user.systemPreferences[i].searchField;
			let criterion = !criteria ? null : criteria[currInput.name];

			this.updateDisplayValue(currInput, criterion);
		}
	}

	// TODO Ideally this should be in srs-field.component, but ngOnInit in that component does not
	// pick up external changes made to the field value
	private updateDisplayValue(currInput: any, criterion: any): string | null {
		let parentMask = null;
		if (!currInput) return parentMask;

		switch (currInput.type_normalized) {
			case "LONG":
			case "INTEGER":
			case "FLOAT":
			case "RADIO":
				currInput.content = criterion?.content == null ? null :
					Array.isArray(criterion.content) ? criterion.content[0] : criterion.content;
				break;

			case "DATE":
			case "DATE_RANGE":	
				// Transform to formatted text
				if (!criterion?.content) {
					currInput.content = null;
				}
				else if (criterion.content.startDate || criterion.content.endDate) {
					const start = criterion.content.startDate ? dayjs(criterion.content.startDate).format(this.dateFormatDisplay) : '';
					const end = criterion.content.endDate ? dayjs(criterion.content.endDate).format(this.dateFormatDisplay) : '';

					currInput.content = start && end ? `${start} to ${end}` : start || end || '';

					// Sync the selected dates in flatpickr
					if (currInput.datepicker) {
						const datesToSet = [];

						if (criterion.content.startDate) {
							datesToSet.push(dayjs(criterion.content.startDate).toDate());
						}
						if (criterion.content.endDate) {
							datesToSet.push(dayjs(criterion.content.endDate).toDate());
						}

						currInput.datepicker.setDate(datesToSet, false); // false to prevent triggering onChange
					}
				}
				else {
					currInput.content = criterion.content;
				}
				break;

			case "STRING":
				currInput.list = criterion?.content == null ? [{ content: null }] : [{ content: criterion.content[0] ?? null }];
				break;

			case "LIST":
				currInput.content = null;
				if (!criterion?.content || !currInput.options) return parentMask;

				const criterionTest = criterion.content.replace(/['"]+/g, '').toLowerCase();
				currInput.content = currInput.options.filter((option: any) =>
					option &&
					(option.value.toLowerCase().replace(/['"]+/g, '') === criterionTest ||
						this.ORMatch(criterionTest, option.value.replace(/['"]+/g, '')))
				);
				currInput.content = currInput.content.length > 1 ? currInput.content : currInput.content[0].value ?? null;
				break;

			case "LIST_MULTI":
				currInput.content = [];
				if (!criterion?.content || criterion.content.length < 1 || !currInput.options) return parentMask;

				currInput.content = currInput.options.filter((option: any) =>
					option &&
					criterion.content.some((currCriterion: string) => {
						const optionValue = option.value.replace(/['"]+/g, '').toLowerCase();
						const criterionValue = currCriterion.replace(/['"]+/g, '').toLowerCase();
						return optionValue === criterionValue || this.ORMatch(criterionValue, optionValue);
					})
				);
				break;
		}
		return parentMask;
	}

	private ORMatch(value: string, content: string): boolean {
		if (!content || !value) {
			return false;
		}
		var parts = value.split(" OR ");
		if (parts && parts.length > 1) {
			return !!(parts.find((x) => { return x.toLowerCase() == content.toLowerCase() }));
		}
		return false;
	}

	// Bound in the <collection> child template
	onUpdateSearch(): void {
		this.searchService.query = this.states.query;
		this.searchService.type = this.states.type?.toString();
		this.searchService.removeFacetFlags(this.searchInputs);
		this.searchService.broadcastUserTriggeredCriteriaUpdateEvent(this.searchInputs, {});
	}

	showDefaultTab(tab: string): void {
		if (this.advancedConfig.browse.id != this.activeTab) {
			this.activeTab = tab;
		}
	}

	/**
	 * Updates the URL with the current state param.
	 */
	toggleTab(): void {
		this.routingService.updateSearchUrl({
			"a": this.activeTab
		}, false, false);
	}

	setActive(params: any): void {
		this.states.browseOpen = false;
		var panel = params.a ? params.a : params;
		if (panel != 'f' && panel != 'c' && panel != 'a' && panel != 's' && panel != 'l' && panel != 'b' && panel != 'm') {
			panel = 'c';
		}
         
		if (this.activeTab !== panel) {
			this.activeTab = panel;

			if ('b' == panel) { this.openBrowse(); }

			/*this.routingService.updateSearchUrl({
				"a": panel
			}, true, false);*/
			// remove browseconfig
			if (params && "browseconfig" in params) {
			  delete params["browseconfig"];
			}
			this.routingService.updateSearchUrl(params, true, false);
		}
	}

	openBrowse(): void {
		this.states.browseOpen = true;
		if (!this.routingService.isSearchRoute()) {
			this.routingService.updateSearchUrl({ 'a': 'c', 'b': '1' }, false, true);
		}
	}

	private listenToDragDestinationEvent(args: any) {
		if (args.destination === 'SearchCtrlFilter') {
			this.dragDestination = args.object;
		}
	}

	private listenToDragLeaveDestinationEvent(args: any) {
		if (args.destination === 'SearchCtrlFilter') {
			let destination = args.object;
			this.dragBefore = this.dragDestination;
			this.dragDestination = this.dragDestination == destination ? null : this.dragDestination;
		}
	}

	private listenToDragTargetEvent(args: any) {
		if (args.destination === 'SearchCtrlFilter') {
			let target = args.object;
			if (this.dragDestination !== null) {
				let targetIndex = this.searchInputs.findIndex((searchInput: any) => { return searchInput === target });
				this.searchInputs.splice(targetIndex, 1);
				let destinationIndex = this.searchInputs.findIndex((searchInput: any) => { return searchInput === this.dragDestination });
				this.searchInputs.splice(destinationIndex + 1, 0, target);
			}
			else if (this.dragBefore !== null) {
				let targetIndex = this.searchInputs.findIndex((searchInput: any) => { return searchInput === target });
				this.searchInputs.splice(targetIndex, 1);
				let destinationIndex = this.searchInputs.findIndex((searchInput: any) => { return searchInput === this.dragBefore });
				this.searchInputs.splice(destinationIndex + 1, 0, target);
			}
			
			this.dragDestination = null;
			this.dragBefore = null;

			this.userService.getUserExtendedPrefs().then(
				(userPrefsObj: any) => {
					var prefs = userPrefsObj.prefs;
					if (prefs) {
						prefs = JSON.parse(prefs);
						prefs.searchFieldOrder = (this.searchInputs ? this.searchInputs.map((field: any) => { return field.name }) : []);
						this.updateExtendedUserPrefs(prefs);
					}
				},
				(err: any) => {
					console.error("Failed to fetch user extended details.", err);
				}
			);
		}
	}

	private updateExtendedUserPrefs(sortprefs: any): void {
		this.userService.updateUserExtendedPrefs(sortprefs).then(
			(saved: boolean) => {
				// empty
			},
			(err: any) => {
				console.error("Failed to update user extended details.", err);
			}
		);
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.updatePanelHeight();
		});
	}

	@HostListener('window:resize', ['$event'])
	onResize(event?: Event) {
		this.updatePanelHeight();
	}

	private updatePanelHeight() {
		const screenHeight = window.innerHeight;

		if (screenHeight > 0) {
			const panelHeader = document.getElementById('left-side-pnel-header');
			const panelHeaderHeight = panelHeader ? panelHeader.offsetHeight : 0;

			let calculatedHeight = this.viewportService.calculateMainContentHeight();
			let panelHeight = calculatedHeight - panelHeaderHeight;
			this.panelStyling = { height: `${panelHeight}px` };
		}
	}
}
